import { getClassNames } from '@ab-core/functions';
import type { BestPrice, CableSnap, PositionPriceObject } from '@ab-core/graphql/dist/index';
import { isLoggedIn } from '@ab-core/hooks';
import { Icon } from '@ab-core/icon';
import useText from '@ab-core/localization/src/useText';
import { BasePrices, BestPriceComponent, PackagingSize } from '@ab-core/prices';
import { getTestIdProp } from '@ab-core/testing';
import type { FC } from 'react';
import React from 'react';
import type { ScalableStyle } from '../../scalableStyles';
import { AddToCart } from './addToCart';
import { LogOutFallBack } from './logOutFallback/logOutFallback';
import { PriceBadge } from './priceBadge';
import type { QuantityPickerStyleType } from './quantityPicker';
import { QuantityPicker } from './quantityPicker';
import { ActionContainer, LimitedAvailabilityWarning, SoldOut } from './styled';

export type OrderContainerStyleType = ScalableStyle<{
    quantityPicker?: QuantityPickerStyleType;
    showAddToCart?: boolean;
    showSecondaryButton?: boolean;
    showPriceBadge?: boolean;
    showPackagingSize?: boolean;
    showBestPrice?: boolean;
    showPrices?: boolean;
}>;

export type OrderContainerProps = {
    onQuantityChange?: (q: number) => void;
    positionPrice?: PositionPriceObject;
    quantityUnit: string;
    sku: string;
    salesUnit: number;
    position: number;
    quantity: number;
    maxQuantity?: number;
    isUnqualifiedContractProduct: boolean;
    isRewardProduct?: boolean;
    positionText?: string;
    chargeNr?: string;
    packagingSize: string | number;
    bestPrice?: BestPrice;
    hideBestPriceOnSalesView: boolean;
    showPrices?: boolean;
    showActions?: boolean;
    projectNumber?: string;
    cartId?: string;
    isSmartphone?: boolean;
    mainId?: string;
    hidePrice?: boolean;
    isPackageItem?: boolean;
    cableSnaps?: Array<CableSnap>;
    supplierId?: string;
    stockQuantity?: number;
    successorProduct?: string;
    ean?: string;
    nocut?: boolean;
};

export const OrderContainer: FC<OrderContainerProps & OrderContainerStyleType> = (props) => {
    const {
        onQuantityChange,
        positionPrice,
        quantityUnit,
        salesUnit,
        sku,
        quantity,
        maxQuantity,
        isUnqualifiedContractProduct,
        isRewardProduct,
        positionText,
        chargeNr,
        scalableStyle,
        packagingSize,
        bestPrice,
        hideBestPriceOnSalesView,
        showActions = true,
        showPrices = true,
        isSmartphone = false,
        cartId,
        mainId,
        cableSnaps,
        hidePrice,
        isPackageItem,
        supplierId,
        stockQuantity,
        successorProduct,
        ean
    } = props;
    const isUserLoggedIn = isLoggedIn();
    const praemienShopSoldOutText = useText('praemienShop.articleSoldOut');
    const soldOutText = useText('productInfos.articleSoldOut');
    const praemienShopLimitedText = useText('praemienShop.articleLimitedAvailable');
    const limitedText = useText('productInfos.articleLimitedAvailable');

    if (!isUserLoggedIn) {
        return <LogOutFallBack sku={sku} />;
    }

    const mobileClassView = getClassNames({
        prefix: 'scalable-product-card',
        modifier: { isSmartphone }
    });
    const adjustedQuantity = quantity % salesUnit === 0 ? quantity : salesUnit;

    const showPackagingSize = !!scalableStyle?.showPackagingSize && !isPackageItem;
    const showBestPrice = scalableStyle?.showBestPrice && bestPrice && !isPackageItem;

    const showPriceBadge = !!scalableStyle?.showPriceBadge && !hidePrice;

    return (
        <>
            {showPrices && !hidePrice && (
                <div {...getTestIdProp('product-card-price')}>
                    <BasePrices {...positionPrice} quantityUnit={quantityUnit} />
                    {showPackagingSize && <PackagingSize packagingSize={packagingSize} isVisible={showPackagingSize} />}
                    {showBestPrice && (
                        <BestPriceComponent {...bestPrice} hideBestPriceOnSalesView={hideBestPriceOnSalesView} />
                    )}
                </div>
            )}
            <ActionContainer className={mobileClassView}>
                {maxQuantity === 0 ? (
                    <SoldOut> {isRewardProduct ? praemienShopSoldOutText : soldOutText}</SoldOut>
                ) : (
                    <>
                        {maxQuantity && (
                            <LimitedAvailabilityWarning>
                                <Icon name="Warning" color="warning" />
                                {isRewardProduct ? praemienShopLimitedText : limitedText}
                            </LimitedAvailabilityWarning>
                        )}
                        {showActions && (
                            <QuantityPicker
                                onQuantityChange={onQuantityChange}
                                quantityUnit={quantityUnit}
                                salesUnit={salesUnit}
                                positionPrice={positionPrice}
                                quantity={quantity}
                                maxQuantity={maxQuantity}
                                sku={sku}
                                scalableStyle={scalableStyle?.quantityPicker}
                                isPackageItem={isPackageItem}
                                cableSnaps={cableSnaps}
                            />
                        )}
                    </>
                )}
                {scalableStyle?.showAddToCart && showActions && (
                    <AddToCart
                        {...positionPrice}
                        cartId={cartId}
                        positionText={positionText}
                        sku={sku}
                        quantity={adjustedQuantity}
                        isUnqualifiedContractProduct={isUnqualifiedContractProduct}
                        showSecondaryButton={scalableStyle?.showSecondaryButton}
                        chargeNr={chargeNr}
                        mainId={mainId}
                        maxQuantity={maxQuantity}
                        supplierId={supplierId}
                        stockQuantity={stockQuantity}
                        successorProduct={successorProduct}
                        ean={ean}
                    />
                )}
            </ActionContainer>
            {showPriceBadge && showActions && <PriceBadge positionPrice={positionPrice} />}
        </>
    );
};
